// src/utils/invoiceGenerator.js
import jsPDF from "jspdf";

/**
 * Generate and download a payment invoice PDF
 * @param {Object} invoiceData - Invoice information
 */
export const generatePaymentInvoice = async (invoiceData) => {
  const {
    invoiceNumber,
    date,
    businessName,
    planName,
    amount,
    currency,
    paymentMethod,
    status,
  } = invoiceData;

  // Create new PDF document
  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  // Colors
  const primaryColor = [102, 126, 234]; // #7F5CFF
  const textColor = [31, 41, 55]; // #1f2937
  const grayColor = [107, 114, 128]; // #6b7280

  // Header - Company name and logo area
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont(undefined, "bold");
  doc.text("INVOICE", margin, 25);

  // Invoice number and date
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(`Invoice #: ${invoiceNumber}`, pageWidth - margin, 20, {
    align: "right",
  });
  doc.text(
    `Date: ${new Date(date).toLocaleDateString()}`,
    pageWidth - margin,
    28,
    { align: "right" }
  );

  // Reset text color
  doc.setTextColor(...textColor);

  // Business Information
  let yPosition = 60;

  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("Billed To:", margin, yPosition);

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.setTextColor(...grayColor);
  yPosition += 8;
  doc.text(businessName, margin, yPosition);

  // Line separator
  yPosition += 15;
  doc.setDrawColor(229, 231, 235); // #e5e7eb
  doc.line(margin, yPosition, pageWidth - margin, yPosition);

  // Invoice Details
  yPosition += 15;
  doc.setTextColor(...textColor);
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("Invoice Details", margin, yPosition);

  // Table header background
  yPosition += 10;
  doc.setFillColor(249, 250, 251); // #f9fafb
  doc.rect(margin, yPosition, pageWidth - 2 * margin, 10, "F");

  // Table headers
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...grayColor);
  yPosition += 7;
  doc.text("Description", margin + 5, yPosition);
  doc.text("Amount", pageWidth - margin - 5, yPosition, { align: "right" });

  // Line under header
  yPosition += 3;
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);

  // Item details
  yPosition += 10;
  doc.setFont(undefined, "normal");
  doc.setTextColor(...textColor);
  doc.setFontSize(10);
  doc.text(`${planName} Subscription`, margin + 5, yPosition);
  doc.text(
    `${currency} ${parseFloat(amount).toFixed(2)}`,
    pageWidth - margin - 5,
    yPosition,
    { align: "right" }
  );

  // Subtotal section
  yPosition += 15;
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);

  yPosition += 10;
  doc.setFontSize(10);
  doc.setTextColor(...grayColor);
  doc.text("Subtotal:", pageWidth - margin - 60, yPosition);
  doc.setTextColor(...textColor);
  doc.text(
    `${currency} ${parseFloat(amount).toFixed(2)}`,
    pageWidth - margin - 5,
    yPosition,
    { align: "right" }
  );

  // Total section
  yPosition += 10;
  doc.setFillColor(249, 250, 251);
  doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, 12, "F");

  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...textColor);
  doc.text("Total:", pageWidth - margin - 60, yPosition + 2);
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text(
    `${currency} ${parseFloat(amount).toFixed(2)}`,
    pageWidth - margin - 5,
    yPosition + 2,
    { align: "right" }
  );

  // Payment Information
  yPosition += 25;
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(...textColor);
  doc.text("Payment Information", margin, yPosition);

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.setTextColor(...grayColor);
  yPosition += 8;
  doc.text(`Payment Method: ${paymentMethod}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Status: ${status}`, margin, yPosition);
  yPosition += 6;
  doc.text(`Transaction ID: ${invoiceNumber}`, margin, yPosition);

  // Footer
  const footerY = pageHeight - 30;
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, footerY, pageWidth - margin, footerY);

  doc.setFontSize(8);
  doc.setTextColor(...grayColor);
  doc.text("Thank you for your business!", pageWidth / 2, footerY + 8, {
    align: "center",
  });
  doc.text(
    "This is a computer-generated invoice.",
    pageWidth / 2,
    footerY + 14,
    { align: "center" }
  );

  // Download the PDF
  doc.save(`invoice-${invoiceNumber}.pdf`);
};

export default generatePaymentInvoice;
